/* eslint-disable linebreak-style */
import * as d3 from 'd3';
import _ from 'lodash';
import './style.scss';

import validateContainer from '../utils/validateContainer';

function isToggleNode(metric) {
  return metric.data.length === 0 && metric.children.length > 0;
}

function drawNode(container, tree, isRoot) {
  const metricTree = this;

  const row = container.append('div').classed('dha-row', true);
  if (tree.children.length !== 0) {
    container.classed('dha-collapsed', !isRoot);
    container.classed('dha-expanded', isRoot);
    row
      .append('div')
      .classed('icon', true)
      .on('click', (event) => {
        metricTree.handleCollapse(container);
        event.stopPropagation();
      });
  }
  const rowContainer = row.append('div').classed('dha-row-container', true);
  rowContainer.append('span').classed('dha-label', true).text(tree.name.replace('/', '/\u200B')); // adds zero width space to fix for google chrome.
  if (isToggleNode(tree)) {
    rowContainer.append('div').classed('dha-toggle', true).text('toggle all');
  } else {
    rowContainer.append('div').classed('dha-circle', true);

    container.classed('dha-selected', _.includes(metricTree.metrics, tree.id));
  }
  rowContainer.on('click', (event) => {
    event.stopPropagation();
    metricTree.toggleNode(tree, container);
  });
  container
    .selectAll('div.node')
    .data(tree.children)
    .join('div')
    .classed('dha-node', true)
    .each(function (d) {
      drawNode.call(metricTree, d3.select(this), d, false);
    });
}

export default class MetricTree {
  constructor(container, model) {
    this.container = validateContainer(container);
    this.compareMode = null;
    this.container.classed('dha-metric-tree', true);
    const header = this.container.append('div').classed('dha-header', true);
    header.append('div').classed('dha-title', true).text('SELECT DATA');
    header
      .append('div')
      .classed('dha-select-multiple', true)
      .text('compare data series')
      .attr('title', 'Click to enable multiple data series on your chart for a selected state.')
      .on('click', () => model.setCompareMode('metrics'));
    this.metrics = [];
    this.tree = null;
    model.registerCallback(({ metricTree, metrics, compareMode, warnings, chartType }) => {
      if (chartType === 1) {
        // states chart
        header
          .select('.dha-select-multiple')
          .style('display', 'none')
          .style('pointer-events', 'none');
      } else {
        header.select('.dha-select-multiple').style('display', null).style('pointer-events', null);
      }
      this.compareMode = compareMode;
      if (compareMode === 'states') {
        header.classed('dha-metrics', false).classed('dha-not-metrics', true);
        this.setRootClass('dha-not-metrics');
      } else {
        header.classed('dha-not-metrics', false).classed('dha-metrics', true);
        this.setRootClass('dha-metrics');
      }
      if (this.tree !== metricTree) {
        this.drawTree(metricTree);
      }
      this.updateMetrics(metrics, warnings.metrics);
    });
    this.model = model;
  }

  // eslint-disable-next-line class-methods-use-this
  setRootClass(className) {
    d3.select('div.dha-node.dha-root')
      .classed('dha-not-metrics', false)
      .classed('dha-metrics', false)
      .classed(className, true);
  }

  toggleNode(tree, container) {
    const hasWarning = container.classed('dha-has-warning');
    if (hasWarning) {
      return;
    }
    if (isToggleNode(tree)) {
      this.model.toggleChildrenMetrics(tree.id);
      container.classed('dha-expanded', true).classed('dha-collapsed', false);
    } else {
      this.model.toggleMetric(tree.id);
    }
  }

  updateWarnings(warnings) {
    d3.selectAll('.dha-row-container').attr('title', null);
    this.container
      .selectAll('.dha-node')
      .classed('dha-has-warning', false)
      .filter((d) =>
        _.includes(
          warnings.map((x) => x.id),
          d.id
        )
      )
      .classed('dha-has-warning', true)
      .select('.dha-row-container')
      .attr('title', (d) => {
        const curWarning = _.find(warnings, ({ id }) => id === d.id);
        return curWarning.warnings
          .map((text) => `\u26A0 ${text}`) // unicode character is a bullet point
          .join('\n');
      });
    if (this.compareMode === 'states') {
      this.container
        .selectAll('.dha-node')
        .classed('dha-not-selectable', false)
        .filter(
          (d) =>
            !_.includes(
              warnings.map((x) => x.id),
              d.id
            )
        )
        .filter(isToggleNode)
        .classed('dha-not-selectable', true);
    } else {
      this.container.selectAll('.dha-node').classed('dha-not-selectable', false);
    }
  }

  updateMetrics(metrics, warnings) {
    this.metrics = metrics;
    this.container
      .selectAll('.dha-node')
      .classed('dha-selected', (d) => _.includes(metrics, d.id))
      .filter((d) => _.includes(metrics, d.id));
    this.updateWarnings(warnings);
  }

  drawTree(tree) {
    this.tree = tree;
    this.container.select('div.dha-node.dha-root').remove();
    const rootContainer = this.container
      .append('div')
      .datum(tree)
      .classed('dha-node', true)
      .classed('dha-root', true);
    drawNode.call(this, rootContainer, tree, true);
  }

  // eslint-disable-next-line class-methods-use-this
  handleCollapse(row) {
    const wasCollapsible = row.classed('dha-expanded');
    row.classed('dha-expanded', !wasCollapsible).classed('dha-collapsed', wasCollapsible);
  }
}
